import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Award, Globe, Users, Sparkles, Filter, Mail, Linkedin } from 'lucide-react';
import { staffAPI } from '../services/api';

export const Team = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        setLoading(true);
        const response = await staffAPI.getAllStaff();
        if (response.success) {
          setStaff(response.staff);
          setFilteredStaff(response.staff);
        }
      } catch (err) {
        setError('Failed to load team members. Please try again later.');
        console.error('Error fetching staff:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, []);

  useEffect(() => {
    let filtered = staff;

    if (activeFilter !== 'all') {
      filtered = filtered.filter(member => member.role === activeFilter);
    }

    if (selectedLocation !== 'all') {
      filtered = filtered.filter(member => 
        member.nationality.toLowerCase() === selectedLocation.toLowerCase() ||
        member.location.toLowerCase().includes(selectedLocation.toLowerCase())
      );
    }

    setFilteredStaff(filtered);
  }, [staff, activeFilter, selectedLocation]);

  const formatJoinDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'project-manager':
        return '👨‍💼';
      case 'senior-consultant':
        return '🎯';
      case 'system-architect':
        return '🏗️';
      case 'software-engineer':
        return '👨‍💻';
      case 'business-analyst':
        return '📊';
      case 'ui-ux-designer':
        return '🎨';
      case 'devops-engineer':
        return '⚙️';
      case 'technical-lead':
        return '🚀';
      case 'data-analyst':
        return '📈';
      case 'mining-engineer':
        return '⛏️';
      default:
        return '👤';
    }
  };

  const getSeniorityColor = (seniority) => {
    switch (seniority.toLowerCase()) {
      case 'principal':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'lead':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'senior':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'mid-level':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'junior':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const roles = [
    { id: 'all', name: 'All Roles', count: staff.length },
    { id: 'project-manager', name: 'Project Manager', count: staff.filter(s => s.role === 'project-manager').length },
    { id: 'senior-consultant', name: 'Senior Consultant', count: staff.filter(s => s.role === 'senior-consultant').length },
    { id: 'system-architect', name: 'System Architect', count: staff.filter(s => s.role === 'system-architect').length },
    { id: 'software-engineer', name: 'Software Engineer', count: staff.filter(s => s.role === 'software-engineer').length },
    { id: 'business-analyst', name: 'Business Analyst', count: staff.filter(s => s.role === 'business-analyst').length },
    { id: 'technical-lead', name: 'Technical Lead', count: staff.filter(s => s.role === 'technical-lead').length },
    { id: 'devops-engineer', name: 'DevOps Engineer', count: staff.filter(s => s.role === 'devops-engineer').length },
  ];

  const locations = [
    { id: 'all', name: 'All Locations' },
    { id: 'uae', name: 'UAE' },
    { id: 'somaliland', name: 'Somaliland' },
    { id: 'india', name: 'India' },
    { id: 'bangladesh', name: 'Bangladesh' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 }
    },
    hover: {
      scale: 1.02,
      y: -5,
      transition: { duration: 0.3 }
    }
  };

  const stats = {
    totalMembers: staff.length,
    countries: [...new Set(staff.map(s => s.nationality))].length,
    averageExperience: Math.round(staff.reduce((sum, s) => sum + s.experience_years, 0) / staff.length),
    seniorMembers: staff.filter(s => ['Senior', 'Lead', 'Principal'].includes(s.seniority)).length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-32 pb-16">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-[#1F3C88] border-t-transparent rounded-full"
            />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-32 pb-16">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops! Something went wrong</h2>
            <p className="text-gray-600 mb-8">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#1F3C88] text-white rounded-lg hover:bg-[#1F3C88]/90 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-32 pb-16">
      <motion.div
        className="container mx-auto px-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Hero Section */}
        <motion.div className="text-center mb-16" variants={itemVariants}>
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#1F3C88]/10 to-[#FF5C8A]/10 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-[#1F3C88]" />
            <span className="text-sm font-semibold text-[#1F3C88]">Meet Our Team</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent mb-6">
            Exceptional Talent
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Our diverse, international team brings together expertise from across the globe. 
            From seasoned consultants to innovative developers, we're united by our commitment to excellence.
          </p>

          {/* Team Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <motion.div
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-lg mb-4 mx-auto">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalMembers}+</div>
              <div className="text-gray-600">Team Members</div>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg mb-4 mx-auto">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.countries}</div>
              <div className="text-gray-600">Countries</div>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-lg mb-4 mx-auto">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.averageExperience}+</div>
              <div className="text-gray-600">Avg. Experience</div>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg mb-4 mx-auto">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.seniorMembers}</div>
              <div className="text-gray-600">Senior Members</div>
            </motion.div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div className="mb-8" variants={itemVariants}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Role Filter */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <Filter className="w-4 h-4 mr-2" />
                Filter by Role
              </h3>
              <div className="flex flex-wrap gap-2">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => setActiveFilter(role.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      activeFilter === role.id
                        ? 'bg-[#1F3C88] text-white shadow-lg transform scale-105'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {role.name} ({role.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Location Filter */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Location</h3>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#1F3C88]"
              >
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Team Grid */}
        <motion.div 
          className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8"
          variants={containerVariants}
        >
          {filteredStaff.map((member, index) => (
            <motion.div
              key={member.id}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden group"
              variants={cardVariants}
              whileHover="hover"
              initial="hidden"
              animate="visible"
              transition={{ delay: index * 0.1 }}
            >
              {/* Member Header */}
              <div className="p-6 pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="text-3xl">{getRoleIcon(member.role)}</div>
                    <div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeniorityColor(member.seniority)}`}>
                        {member.seniority}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-[#1F3C88]">{member.experience_years}y</div>
                    <div className="text-xs text-gray-500">Experience</div>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-[#1F3C88] transition-colors">
                  {member.name}
                </h3>
                
                <p className="text-[#1F3C88] font-semibold text-sm mb-3">
                  {member.role.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>

                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {member.bio}
                </p>

                {/* Location & Nationality */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>{member.nationality}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>Since {formatJoinDate(member.join_date).split(' ')[1]}</span>
                  </div>
                </div>
              </div>

              {/* Education & Certifications */}
              <div className="px-6 pb-4">
                <div className="mb-3">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Education</h4>
                  <p className="text-sm text-gray-700">{member.education}</p>
                </div>

                {member.certifications.length > 0 && (
                  <div className="mb-3">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Certifications</h4>
                    <div className="flex flex-wrap gap-1">
                      {member.certifications.slice(0, 3).map((cert, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-100"
                        >
                          {cert}
                        </span>
                      ))}
                      {member.certifications.length > 3 && (
                        <span className="px-2 py-1 bg-gray-50 text-gray-500 text-xs rounded-md border border-gray-100">
                          +{member.certifications.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Specializations */}
              <div className="px-6 pb-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Specializations</h4>
                <div className="flex flex-wrap gap-1">
                  {member.specializations.slice(0, 4).map((spec, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-gradient-to-r from-[#1F3C88]/10 to-[#FF5C8A]/10 text-[#1F3C88] text-xs rounded-md"
                    >
                      {spec}
                    </span>
                  ))}
                  {member.specializations.length > 4 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md">
                      +{member.specializations.length - 4} more
                    </span>
                  )}
                </div>
              </div>

              {/* Languages */}
              <div className="px-6 pb-6">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Languages</h4>
                <div className="flex flex-wrap gap-1">
                  {member.languages.map((lang, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md border border-green-100"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="px-6 pb-6 pt-0 border-t border-gray-100">
                <div className="flex items-center justify-between text-sm pt-4">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span className="font-medium">{member.location}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Joined {formatJoinDate(member.join_date)}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* No Results Message */}
        {filteredStaff.length === 0 && (
          <motion.div
            className="text-center py-16"
            variants={itemVariants}
          >
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No team members found</h3>
            <p className="text-gray-500">Try adjusting your filters to see more results.</p>
          </motion.div>
        )}

        {/* Call to Action */}
        <motion.div
          className="text-center mt-16 py-12 bg-gradient-to-r from-[#1F3C88]/5 to-[#FF5C8A]/5 rounded-3xl"
          variants={itemVariants}
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Join Our World-Class Team
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            We're always looking for exceptional talent to join our growing team. 
            If you're passionate about making an impact, we'd love to hear from you.
          </p>
          <motion.a
            href="/contact"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg transition-all duration-300"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>Get In Touch</span>
            <Mail className="w-5 h-5" />
          </motion.a>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Team;